import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useHistory, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Page from '@afosto/app-components-react/Page';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import { tenants as tenantsApi } from '@afosto/iam/api/Tenants';
import { users as usersApi } from '@afosto/iam/api/Users';
import translations from './translations';

const ViewPurgeRequest = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['iam:tenants:read']);

  const intl = useIntl();
  const { token } = useParams();
  const history = useHistory();
  const confirm = useConfirm();
  const [purgeData, setPurgeData] = useState({});
  const [isFetchingPurgeData, setIsFetchingPurgeData] = useState(true);
  const [isPurgingData, setIsPurgingData] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleStartPurge = async () => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.confirmPurgeData.title),
        description: intl.formatMessage(translations.confirmPurgeData.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.confirmPurgeData.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.confirmPurgeData.buttons.purge),
            response: 'purge',
            type: 'submit',
            variant: 'contained',
            color: 'secondary',
          },
        ],
      });

      if (confirmResponse === 'purge') {
        setIsPurgingData(true);
        await tenantsApi.startPurge(token);
        enqueueSnackbar(intl.formatMessage(translations.feedback.purgeStarted), {
          variant: 'success',
        });
      }
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.purgeNotStarted), {
        variant: 'error',
      });
    } finally {
      setIsPurgingData(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchPurgeRequest = async purgeToken => {
      setIsFetchingPurgeData(true);

      try {
        const tokenResponse = await tenantsApi.viewPurgeToken(purgeToken);
        const { data: tokenData } = tokenResponse;

        const requesterResponse = await usersApi.getUser(tokenData?.requester).catch(() => null);
        const { data: requesterData = {} } = requesterResponse ?? {};

        const signerResponse = await usersApi.getUser(tokenData?.signer);
        const { data: signerData } = signerResponse;

        if (isMounted) {
          setPurgeData({ ...tokenData, requester: requesterData, signer: signerData });
        }
      } catch (error) {
        if (isMounted) {
          setPurgeData(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingPurgeData(false);
        }
      }
    };

    if (token) {
      fetchPurgeRequest(token);
    }

    return () => {
      isMounted = false;
    };
  }, [token]);

  const { requester = {}, domains = [], expires_at: expiresAt, is_used: isUsed } = purgeData;
  const isExpired = new Date().getTime() > new Date(expiresAt).getTime();

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)}>
        <UnauthorizedComponent />
      </Page>
    );
  }

  if (isFetchingPurgeData) {
    return (
      <Page isLoading>
        <Card>
          <CardContent>
            <SkeletonBody lines={5} />
          </CardContent>
        </Card>
      </Page>
    );
  }

  if (isUsed && !isFetchingPurgeData) {
    return (
      <Page title={intl.formatMessage(translations.usedTitle)}>
        <Card>
          <CardContent>
            <Typography paragraph>
              {intl.formatMessage(translations.usedDescription, { requester: requester?.email })}
            </Typography>
            <Box display="flex" justifyContent="space-between" mt={4}>
              <Button onClick={() => history.push('/')} variant="outlined">
                {intl.formatMessage(translations.actions.cancel)}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Page>
    );
  }

  if (isExpired && !isFetchingPurgeData) {
    return (
      <Page title={intl.formatMessage(translations.expiredTitle)}>
        <Card>
          <CardContent>
            <Typography paragraph>
              {intl.formatMessage(translations.expiredDescription, { requester: requester?.email })}
            </Typography>
            <Typography variant="overline">
              {intl.formatMessage(translations.dataToPurge)}
            </Typography>
            <Grid container spacing={1}>
              {domains.map(domain => (
                <Grid item>
                  <Chip label={domain} color="primary" variant="outlined" />
                </Grid>
              ))}
            </Grid>
            <Box display="flex" justifyContent="space-between" mt={4}>
              <Button onClick={() => history.push('/')} variant="outlined">
                {intl.formatMessage(translations.actions.cancel)}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Page>
    );
  }

  return (
    <Page title={intl.formatMessage(translations.title)}>
      <LoadingWrapper isLoading={isPurgingData}>
        <Card>
          <CardContent>
            <Typography paragraph>
              {intl.formatMessage(translations.description, { requester: requester?.email })}
            </Typography>
            <Typography paragraph>
              {intl.formatMessage(translations.expiryNotice, {
                date: intl.formatRelative(expiresAt),
              })}
            </Typography>
            <Typography variant="overline">data to purge</Typography>
            <Grid container spacing={1}>
              {domains.map(domain => (
                <Grid item>
                  <Chip label={domain} color="primary" variant="outlined" />
                </Grid>
              ))}
            </Grid>
            <Box display="flex" justifyContent="space-between" mt={4}>
              <Button onClick={() => history.push('/')} variant="outlined">
                {intl.formatMessage(translations.actions.cancel)}
              </Button>
              <Button onClick={handleStartPurge} variant="contained" color="secondary">
                {intl.formatMessage(translations.actions.purge)}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </LoadingWrapper>
    </Page>
  );
};

export default ViewPurgeRequest;
